import { User2 } from 'lucide-react';

import { useTranslations } from '@/hooks/use-translations';
import { parseHtml } from '@/utils/html-parser';

/**
 * Props for the AnamneseCard component
 */
interface AnamneseCardProps {
  /** Display variant - 'slide' for compact in-slide view, 'modal' for full detailed view */
  variant?: 'slide' | 'modal';
  /** Patient gender */
  gender?: 'woman' | 'man';
  /** Patient title/name */
  title: string;
  /** List of medical history items */
  descriptionList?: string[];
}

/**
 * AnamneseCard Component
 *
 * Displays patient anamnesis (medical history) information in two different formats:
 * - **slide variant**: Compact card for displaying inside case slides
 * - **modal variant**: Detailed card with gender indicator for modal/panel views
 *
 * Features:
 * - Gender-specific color coding (pink for female, blue for male)
 * - Multi-column layout for medical history items without cross-column alignment
 * - Bullet-point list formatting
 */
export const AnamneseCard = ({
  variant = 'modal',
  gender,
  title,
  descriptionList = [],
}: AnamneseCardProps) => {
  const { t } = useTranslations();
  const Icon = User2;
  const genderSymbol = gender === 'woman' ? '♀' : '♂';
  const genderColor = gender === 'woman' ? 'bg-pink-500' : 'bg-blue-500';
  const genderIconColor =
    gender === 'woman' ? 'bg-pink-100 text-pink-600' : 'bg-blue-100 text-blue-600';

  // Slide variant - compact version for slide display
  if (variant === 'slide') {
    return (
      <div className="flex h-full w-full items-center justify-center px-4">
        <div className="w-full max-w-4xl">
          <div className="rounded-xl border border-border bg-card p-4 shadow-white sm:p-6">
            <div className="flex flex-col items-start gap-4 sm:flex-row sm:gap-6">
              <div className="flex-shrink-0">
                <div
                  className={`flex h-14 w-14 items-center justify-center rounded-full sm:h-16 sm:w-16 ${genderIconColor}`}
                >
                  <Icon className="h-7 w-7 sm:h-8 sm:w-8" />
                </div>
              </div>

              <div className="w-full flex-1">
                <h2 className="mb-3 text-xl font-semibold text-card-foreground sm:mb-4 sm:text-2xl">
                  {parseHtml(title)}
                </h2>

                <div className="columns-1 gap-4 sm:columns-2">
                  {descriptionList.map((item, index) => (
                    <div
                      key={index}
                      className="mb-2 flex break-inside-avoid items-start gap-2 text-sm text-muted-foreground sm:text-base"
                    >
                      <span className="mt-1 flex-shrink-0 text-xs text-primary">•</span>
                      <span className="flex-1 leading-snug">{parseHtml(item)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Modal variant - detailed version for modal display
  return (
    <div className="flex h-full w-full items-center justify-center p-8">
      <div className="w-full max-w-4xl rounded-xl bg-white p-8 shadow-white">
        {/* Patient Header */}
        <div className="mb-8 flex items-center gap-6">
          <div
            className={`h-20 w-20 rounded-full ${genderColor} flex items-center justify-center text-4xl text-white shadow-lg`}
          >
            {genderSymbol}
          </div>
          <div>
            <h2 className="mb-1 font-heading text-4xl text-livelinx-purple200">
              {parseHtml(title)}
            </h2>
            <p className="font-body text-lg capitalize text-livelinx-grey200">
              {gender === 'woman'
                ? t('caseViewer.anamnese.gender.female')
                : t('caseViewer.anamnese.gender.male')}
            </p>
          </div>
        </div>

        {/* Medical History */}
        {descriptionList.length > 0 && (
          <div className="rounded-lg bg-livelinx-purple50 p-6">
            <h4 className="mb-4 font-heading text-xl text-livelinx-purple200">
              {t('caseViewer.anamnese.medicalHistory')}
            </h4>
            <div className="columns-1 gap-6 md:columns-2">
              {descriptionList.map((item, index) => (
                <div
                  key={index}
                  className="mb-3 flex break-inside-avoid items-start gap-3 font-body text-base text-livelinx-text"
                >
                  <span className="mt-1 flex-shrink-0 text-xl text-primary">•</span>
                  <span>{parseHtml(item)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
